import { MAX_NOTE_LINKS, MAX_NOTE_PLAYERS, type DemoScope, type NoteLink, type NoteLinkInput, type NotePlayer, type NoteSide, type NoteTeam, type Replay } from "@69ly/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Icon } from "../components/Icon.tsx";
import { Select } from "../components/Select.tsx";
import { useMatches } from "../lib/demo-queries.ts";
import { isMapId, mapInfo } from "@69ly/maps";

const mapName = (map: string) => (isMapId(map) ? mapInfo(map).name : map);

/**
 * What a note is about and where it points, shared by a new note and one being changed:
 * the side (T or CT), one of the demo's two teams, and links to other matches' maps and rounds.
 */
export interface NoteAbout {
  side: NoteSide | null;
  team: 0 | 1 | null;
  links: NoteLinkInput[];
}
export const NO_ABOUT: NoteAbout = { side: null, team: null, links: [] };

/** A link as it opens: a round of a map, a map, or the match. */
export function linkUrl(l: { matchId: string; demoId: string | null; round: number | null }): string {
  if (!l.demoId) return `/matches/${l.matchId}`;
  return l.round === null ? `/demos/${l.demoId}` : `/demos/${l.demoId}?r=${l.round + 1}`;
}

/** Side and team pickers, and the links. `teamNames`: the demo's teams 0 and 1 as the match names them. */
export function NoteAboutFields({ about, onChange, teamNames, scope }: { about: NoteAbout; onChange: (a: NoteAbout) => void; teamNames: [string, string]; scope: DemoScope | null }) {
  return (
    <>
      <NoteSideTeam about={about} onChange={onChange} teamNames={teamNames} />
      <LinksEditor links={about.links} onChange={(links) => onChange({ ...about, links })} scope={scope} />
    </>
  );
}

/** Side and team, side by side, each a segmented control under its name. */
export function NoteSideTeam({ about, onChange, teamNames }: { about: NoteAbout; onChange: (a: NoteAbout) => void; teamNames: [string, string] }) {
  const { t } = useTranslation();
  return (
    <div className="note-about-row">
      <fieldset className="form-field">
        <legend className="note-legend">{t("notes.side")}</legend>
        <div className="segmented segmented-small">
          {([null, "t", "ct"] as const).map((s) => (
            <button key={s ?? "none"} type="button" className={`seg${s ? ` side-seg-${s}` : ""}`} aria-pressed={about.side === s} onClick={() => onChange({ ...about, side: s })}>
              {s ? t(`live.sideShort.${s}`) : t("notes.any")}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset className="form-field">
        <legend className="note-legend">{t("notes.team")}</legend>
        <div className="segmented segmented-small note-team-seg">
          {([null, 0, 1] as const).map((k) => (
            <button key={k ?? "none"} type="button" className="seg" aria-pressed={about.team === k} onClick={() => onChange({ ...about, team: k })}>
              {k === null ? t("notes.any") : teamNames[k]}
            </button>
          ))}
        </div>
      </fieldset>
    </div>
  );
}

/**
 * Who a note is about: a row of numbered toggles per side (the numbers of the map and the
 * kill feed), the picked players' names under them. A click on a player on the map toggles
 * them too (the viewer does that).
 */
export function PlayerToggles({ replay, tTeam, numbers, picked, onChange }: { replay: Replay; tTeam: 0 | 1; numbers: number[]; picked: number[]; onChange: (p: number[]) => void }) {
  const { t } = useTranslation();
  const toggle = (i: number) => onChange(picked.includes(i) ? picked.filter((x) => x !== i) : picked.length >= MAX_NOTE_PLAYERS ? picked : [...picked, i]);
  const sideOf = (i: number) => (replay.players[i]?.team === tTeam ? "t" : "ct");
  return (
    <fieldset className="form-field note-players">
      <legend className="note-legend">{t("notes.players.label")}</legend>
      {(["t", "ct"] as const).map((side) => (
        <div key={side} className="note-roster-row" role="group" aria-label={t("notes.players.side", { side: t(`live.sideShort.${side}`) })}>
          <span className={`note-roster-side side-${side}`} aria-hidden="true">
            {t(`live.sideShort.${side}`)}
          </span>
          {replay.players.map((p, i) =>
            sideOf(i) !== side ? null : (
              <button key={i} type="button" className={`chip note-num side-${side}`} aria-pressed={picked.includes(i)} aria-label={p.name} title={p.name} onClick={() => toggle(i)}>
                {numbers[i]}
              </button>
            ),
          )}
        </div>
      ))}
      <div className="note-picked" aria-live="polite">
        {picked.length === 0 ? (
          <span className="muted small">{t("notes.players.none")}</span>
        ) : (
          picked.map((i) => (
            <button
              key={i}
              type="button"
              className={`chip chip-small note-player side-${sideOf(i)}`}
              aria-label={t("notes.players.remove", { name: replay.players[i]?.name ?? "" })}
              onClick={() => toggle(i)}
            >
              <span className="note-player-num">{numbers[i]}</span>
              {replay.players[i]?.name}
              <Icon name="close" size={12} />
            </button>
          ))
        )}
      </div>
    </fieldset>
  );
}

/** The seconds and clock of a note's moment within its round. */
const clockOf = (s: number) => {
  const v = Math.max(0, Math.round(s));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};

/**
 * How long a note lasts: a moment, or a while (its seconds, where it ends, and "Until now"
 * to end it where the replay is). `now`: the replay's time in the same round, else null.
 */
export function LastsField({ start, max, now, value, onChange }: { start: number; max: number; now: number | null; value: number | null; onChange: (v: number | null) => void }) {
  const { t } = useTranslation();
  const room = Math.max(1, Math.floor(max - start));
  const untilNow = now !== null && now - start >= 0.5 ? Math.min(room, Math.round((now - start) * 10) / 10) : null;
  return (
    <fieldset className="form-field">
      <legend className="note-legend">{t("notes.lasts.label")}</legend>
      <div className="note-lasts">
        <div className="segmented segmented-small">
          <button type="button" className="seg" aria-pressed={value === null} onClick={() => onChange(null)}>
            {t("notes.lasts.moment")}
          </button>
          <button type="button" className="seg" aria-pressed={value !== null} onClick={() => onChange(value ?? Math.min(room, untilNow ?? 10))}>
            {t("notes.lasts.while")}
          </button>
        </div>
        {value !== null && (
          <>
            <label className="field note-seconds">
              <span className="visually-hidden">{t("notes.lasts.seconds")}</span>
              <input
                className="mono"
                type="number"
                inputMode="decimal"
                min={1}
                max={room}
                step={1}
                value={value}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n)) onChange(Math.min(room, Math.max(1, n)));
                }}
              />
            </label>
            <span className="muted small">{t("notes.lasts.to", { clock: clockOf(start + value) })}</span>
            <button type="button" className="btn btn-secondary btn-small" disabled={untilNow === null} title={t("notes.lasts.untilNowHelp")} onClick={() => untilNow !== null && onChange(untilNow)}>
              {t("notes.lasts.untilNow")}
            </button>
          </>
        )}
      </div>
    </fieldset>
  );
}

/** The players a note is about, as small chips with their number (as the map shows them). */
export function NotePlayers({ players, numbers, sideOf }: { players: NotePlayer[]; numbers?: number[]; sideOf?: (i: number) => "t" | "ct" | null }) {
  if (players.length === 0) return null;
  return (
    <span className="note-players-row">
      {players.map((p) => {
        const side = sideOf?.(p.index) ?? null;
        return (
          <span key={p.index} className={`chip chip-small note-player${side ? ` side-${side}` : ""}`}>
            {numbers?.[p.index] !== undefined && <span className="note-player-num">{numbers[p.index]}</span>}
            {p.name || "?"}
          </span>
        );
      })}
    </span>
  );
}

/** Where a note is in its round: "0:25", or "0:25–0:58" for one that lasts. */
export function noteWhen(n: { seconds: number | null; endSeconds: number | null }): string {
  if (n.seconds === null) return "";
  return n.endSeconds !== null ? `${clockOf(n.seconds)}–${clockOf(n.endSeconds)}` : clockOf(n.seconds);
}

/** Links to other matches: chosen match, then optionally a map and a round. */
export function LinksEditor({ links, onChange, scope }: { links: NoteLinkInput[]; onChange: (l: NoteLinkInput[]) => void; scope: DemoScope | null }) {
  const { t } = useTranslation();
  const matches = useMatches(scope).data ?? [];
  const [open, setOpen] = useState(false);
  const [matchId, setMatchId] = useState<string | null>(null);
  const [demoId, setDemoId] = useState<string | null>(null);
  const [round, setRound] = useState("");
  const match = matches.find((m) => m.id === matchId) ?? null;
  const map = match?.maps.find((m) => m.demoId === demoId) ?? null;
  const roundNumber = Number(round);
  const roundOk = round === "" || (Number.isInteger(roundNumber) && roundNumber >= 1 && (!map || roundNumber <= map.rounds));
  const reset = () => {
    setOpen(false);
    setMatchId(null);
    setDemoId(null);
    setRound("");
  };
  const describe = (l: NoteLinkInput) => {
    const m = matches.find((x) => x.id === l.matchId);
    const mp = m?.maps.find((x) => x.demoId === l.demoId);
    return [m?.title ?? t("notes.aMatch"), mp ? mapName(mp.map) : null, l.round !== null && l.round !== undefined ? t("notes.roundN", { n: l.round + 1 }) : null].filter(Boolean).join(" · ");
  };
  return (
    <div className="note-links-edit">
      {links.length > 0 && (
        <ul className="note-links" aria-label={t("notes.links")}>
          {links.map((l, i) => (
            <li key={`${l.matchId}-${l.demoId}-${l.round}-${i}`} className="chip chip-small">
              <Icon name="link" size={12} />
              <span>{describe(l)}</span>
              <button type="button" className="chip-remove" aria-label={t("notes.removeLink", { link: describe(l) })} onClick={() => onChange(links.filter((_, j) => j !== i))}>
                <Icon name="close" size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {!open && links.length < MAX_NOTE_LINKS && (
        <button type="button" className="chip" aria-pressed={false} onClick={() => setOpen(true)}>
          <Icon name="link" size={14} />
          {links.length ? t("notes.addAnotherLink") : t("notes.addLink")}
        </button>
      )}
      {open && (
        <div className="note-link-picker" role="group" aria-label={t("notes.addLink")}>
          <Select
            label={t("notes.linkMatch")}
            value={matchId}
            placeholder={t("notes.linkMatch")}
            groups={[{ options: matches.map((m) => ({ value: m.id, label: m.title, hint: m.maps.map((x) => mapName(x.map)).join(", ") })) }]}
            onChange={(v) => {
              setMatchId(v);
              setDemoId(null);
              setRound("");
            }}
          />
          {match && (
            <Select
              label={t("notes.linkMap")}
              value={demoId ?? ""}
              placeholder={t("notes.wholeMatch")}
              groups={[
                {
                  options: [{ value: "", label: t("notes.wholeMatch") }, ...match.maps.map((m) => ({ value: m.demoId, label: `${m.number}. ${mapName(m.map)}`, hint: `${m.score[0]}–${m.score[1]}` }))],
                },
              ]}
              onChange={(v) => {
                setDemoId(v || null);
                setRound("");
              }}
            />
          )}
          {map && (
            <label className="field note-link-round">
              <span>{t("notes.linkRound", { of: map.rounds })}</span>
              <input type="number" inputMode="numeric" min={1} max={map.rounds} value={round} placeholder={t("notes.anyRound")} onChange={(e) => setRound(e.target.value)} aria-invalid={!roundOk} />
            </label>
          )}
          <div className="row">
            <button
              type="button"
              className="btn btn-secondary btn-small"
              disabled={!matchId || !roundOk}
              onClick={() => {
                onChange([...links, { matchId: matchId!, demoId, round: map && round !== "" ? roundNumber - 1 : null }]);
                reset();
              }}
            >
              {t("notes.linkAdd")}
            </button>
            <button type="button" className="btn btn-quiet btn-small" onClick={reset}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** A note's side, team and links, as small tags (the links open where they point). */
export function NoteTags({ side, team, links }: { side: NoteSide | null; team: NoteTeam | null; links: NoteLink[] }) {
  const { t } = useTranslation();
  if (!side && !team && links.length === 0) return null;
  return (
    <span className="note-tags">
      {side && <span className={`live-side side-${side}`}>{t(`live.sideShort.${side}`)}</span>}
      {team && (
        <span className="chip chip-small note-team" title={t("notes.aboutTeam", { team: team.name })}>
          {team.name}
        </span>
      )}
      {links.map((l, i) =>
        l.missing ? (
          <span key={i} className="chip chip-small muted" title={t("notes.linkGone")}>
            <Icon name="link" size={12} />
            {t("notes.linkGone")}
          </span>
        ) : (
          <Link key={i} to={linkUrl(l)} className="chip chip-small note-link" onClick={(e) => e.stopPropagation()}>
            <Icon name="link" size={12} />
            {[l.matchTitle, l.map ? mapName(l.map) : null, l.round !== null ? t("notes.roundN", { n: l.round + 1 }) : null].filter(Boolean).join(" · ")}
          </Link>
        ),
      )}
    </span>
  );
}

/** The links a note holds, as sent back when it is changed. */
export const linkInputs = (links: NoteLink[]): NoteLinkInput[] => links.filter((l) => !l.missing).map((l) => ({ matchId: l.matchId, demoId: l.demoId, round: l.round }));
